import type { ViewerShell } from '../../shell';
import type { AssistantApi } from './api';
import { conversationStudy } from './context';
import { askErrorText } from './panel';
import type { SimilarCase } from './shapes';

/**
 * "Casos similares" (contract v2 §4): a search over the org's own accepted findings and signed
 * impressions (ris answers it with Postgres full-text, no external call, never this patient). Each
 * result shows only what ris let through `egress`: modality, relative date, match and snippet.
 *
 * A result is never opened into this session. Every case here is ANOTHER patient's study; loading it
 * would put it in the study browser beside this patient's own studies, where nothing tells a reader
 * the two apart. The ticket covers only this patient anyway, so `can_open` is deliberately ignored.
 */

export const SIMILAR_TEXT = {
  tool: 'Casos similares',
  title: 'Casos similares de la organización',
  placeholder: 'Buscar por hallazgo o impresión…',
  inputLabel: 'Buscar casos similares',
  search: 'Buscar',
  searching: 'Buscando…',
  empty: 'No se encontraron casos similares.',
  match: 'coincidencia',
  failed: 'No fue posible buscar casos similares.',
} as const;

const MAX_QUERY = 200;
const PERCENT = 100;

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function caseHead(item: SimilarCase): string {
  const parts = [item.modality, item.dateRelative].filter((p): p is string => !!p);
  if (item.score !== null && item.score > 0 && item.score <= 1) parts.push(`${SIMILAR_TEXT.match} ${Math.round(item.score * PERCENT)} %`);
  return parts.join(' · ');
}

function renderCase(item: SimilarCase): HTMLElement {
  const row = el('li', 'assistant-similar-item');
  const head = caseHead(item);
  if (head) row.appendChild(el('div', 'assistant-similar-head', head));
  if (item.snippet) row.appendChild(el('div', 'assistant-similar-snippet', item.snippet));
  return row;
}

async function search(shell: ViewerShell, api: AssistantApi, query: string, ui: { button: HTMLButtonElement; results: HTMLElement }): Promise<void> {
  if (ui.button.disabled) return;
  ui.button.disabled = true;
  ui.button.textContent = SIMILAR_TEXT.searching;
  try {
    const items = await api.similar(conversationStudy(shell), query.trim().slice(0, MAX_QUERY));
    if (items.length === 0) {
      ui.results.replaceChildren(el('p', 'assistant-empty', SIMILAR_TEXT.empty));
      return;
    }
    const list = el('ul', 'assistant-similar-list');
    items.forEach((item) => list.appendChild(renderCase(item)));
    ui.results.replaceChildren(list);
  } catch (error) {
    console.warn('[viewer] similar cases failed', error);
    shell.toast(askErrorText(error) || SIMILAR_TEXT.failed, 'error');
  } finally {
    ui.button.disabled = false;
    ui.button.textContent = SIMILAR_TEXT.search;
  }
}

/** The card the "Casos similares" tool adds to the conversation. */
export function similarCasesCard(shell: ViewerShell, api: AssistantApi): HTMLElement {
  const card = el('div', 'assistant-similar-card');
  card.appendChild(el('div', 'assistant-analysis-title', SIMILAR_TEXT.title));
  const form = el('form', 'assistant-similar-form');
  const input = el('input', 'assistant-similar-input');
  input.type = 'search';
  input.maxLength = MAX_QUERY;
  input.placeholder = SIMILAR_TEXT.placeholder;
  input.setAttribute('aria-label', SIMILAR_TEXT.inputLabel);
  const button = el('button', 'assistant-btn assistant-similar-search', SIMILAR_TEXT.search);
  button.type = 'submit';
  form.append(input, button);
  const results = el('div', 'assistant-similar-results');
  results.setAttribute('aria-live', 'polite');
  card.append(form, results);
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void search(shell, api, input.value, { button, results });
  });
  return card;
}
